import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { Calendar } from 'primereact/calendar';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { AssignmentFilters } from './assignments-side-bar-filter.type';
import { CourseStatus } from '../../../../../api/types/programs/course/course-status';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

interface AssignmentsSideBarFilterProps {
  onSearch: (filters: AssignmentFilters) => void;
}

export const AssignmentsSideBarFilter = observer(
  ({ onSearch }: AssignmentsSideBarFilterProps) => {
    const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
    const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [selectedCourseStatuses, setSelectedCourseStatuses] = useState<
      CourseStatus[]
    >([]);
    const [dueDateStart, setDueDateStart] = useState<Date | null>(null);
    const [dueDateEnd, setDueDateEnd] = useState<Date | null>(null);
    const [isGradesPublished, setIsGradesPublished] = useState<boolean | null>(
      null,
    );

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    const courseStatuses = [
      CourseStatus.INIT,
      CourseStatus.ACTIVE,
      CourseStatus.CLOSED,
      CourseStatus.FINISHED,
    ];

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranchIds((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const handleProgramToggle = (programId: number) => {
      setSelectedProgramIds((prev) =>
        prev.includes(programId)
          ? prev.filter((p) => p !== programId)
          : [...prev, programId],
      );
    };

    const handleYearToggle = (yearStr: string) => {
      const year = parseInt(yearStr);
      setSelectedYears((prev) =>
        prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
      );
    };

    const handleCourseStatusToggle = (status: CourseStatus) => {
      setSelectedCourseStatuses((prev) =>
        prev.includes(status)
          ? prev.filter((s) => s !== status)
          : [...prev, status],
      );
    };

    const handleGradesPublishedChange = (
      checked: boolean,
      isPublished: boolean,
    ) => {
      if (checked) {
        setIsGradesPublished(isPublished);
      } else {
        // If unchecking the current state, set to null
        if (
          (isPublished && isGradesPublished === true) ||
          (!isPublished && isGradesPublished === false)
        ) {
          setIsGradesPublished(null);
        }
      }
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      dateContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      checkboxContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
      checkboxItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
      },
    };

    const handleSearch = () => {
      const filters: AssignmentFilters = {
        branchIds: selectedBranchIds,
        abstractProgramIds: selectedProgramIds,
        startYears: selectedYears,
        courseStatuses: selectedCourseStatuses,
        dueDateStart: dueDateStart,
        dueDateEnd: dueDateEnd,
        isGradesPublished,
      };

      onSearch(filters);
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranchIds}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="תכניות לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={selectedProgramIds}
            onItemToggle={handleProgramToggle}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            searchPlaceholder="חפש תכנית..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה בתוכנית"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={handleYearToggle}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס קורס"
            items={courseStatuses}
            selectedItems={selectedCourseStatuses}
            onItemToggle={handleCourseStatusToggle}
            getItemLabel={(status) => CourseStatus.getDisplayText(status)}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />

          <CustomExpander>
            <div>תאריך אחרון להגשה</div>
            <div style={styles.dateContainer}>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>מתאריך</label>
                <Calendar
                  value={dueDateStart}
                  onChange={(e) => setDueDateStart(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>עד תאריך</label>
                <Calendar
                  value={dueDateEnd}
                  onChange={(e) => setDueDateEnd(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>סטטוס ציונים</div>
            <div style={styles.checkboxContainer}>
              <div style={styles.checkboxItem}>
                <Checkbox
                  checked={isGradesPublished === true}
                  onChange={(e) =>
                    handleGradesPublishedChange(e.checked || false, true)
                  }
                />
                <label>פורסמו ציונים</label>
              </div>
              <div style={styles.checkboxItem}>
                <Checkbox
                  checked={isGradesPublished === false}
                  onChange={(e) =>
                    handleGradesPublishedChange(e.checked || false, false)
                  }
                />
                <label>לא פורסמו ציונים</label>
              </div>
            </div>
          </CustomExpander>
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  },
);
